// The six metrics the Productivity Chart plots, described in one place.
//
// A metric is named by a great many things - its toggle chip, its axis, its series, the readout
// row, the band-height layout, the comparison halo, the empty-band test - and every one of them
// needs the same handful of facts about it: which band it shares, which axis it is drawn on, what
// colour it is, which Sensor has to exist for it to mean anything, which granularities can express
// it, and which bucket fields decide whether its band holds anything at all. Stated once here, a
// metric is one entry rather than eight parallel lists, any one of which can be missed silently.

import { CHART_SERIES_COLORS } from './productivityColors';
import type { ProductivityGranularity } from '@/plugins/store/actions/queries/active-groups';
import { DAILY_GAIN_G_RANGE, GROWTH_INDEX_RANGE, type PlausibleRange } from './plausibleRange';

// The y axis array's own contract, by name. The chart declares its axes in exactly this order and
// every series, halo and axis reference addresses one by name - so reordering the array moves every
// reference with it rather than silently mis-plotting five files' worth of bare integers.
export const PRODUCTIVITY_AXIS = {
    productionGroups: 0,
    feedMixes: 1,
    weight: 2,
    dailyGain: 3,
    water: 4,
    growthIndex: 5,
    deadCount: 6,
} as const;

export type AxisId = keyof typeof PRODUCTIVITY_AXIS;

/**
 * Which field carries the metered water for the granularity being drawn. The backend states it in
 * litres for an hour bucket and per pig for a day bucket - two different units, so two different
 * fields rather than one whose meaning the reader has to infer from the granularity toggle.
 */
export type WaterActualKey = 'water_litres' | 'water_per_pig';

export function waterActualKey(granularity: ProductivityGranularity): WaterActualKey {
    return granularity === 'hour' ? 'water_litres' : 'water_per_pig';
}

// The plot box a metric is drawn in. Several metrics share one - weight with Daily Gain, water with
// its norm - which is what makes a band's visibility and its emptiness questions about the group
// rather than about any single metric. See productivityBands.ts for how the boxes are sized.
export type MetricBandId = 'weight' | 'water' | 'growthIndex' | 'deadCount';

export const PRODUCTIVITY_BAND_IDS: readonly MetricBandId[] = ['weight', 'water', 'growthIndex', 'deadCount'];

// The Sensor a metric needs before it can ever carry a reading. A metric derived from something
// other than telemetry - dead count comes from pig movements - declares none.
export type MetricSensor = 'weight' | 'water';

type MetricDefinition = {
    band: MetricBandId;
    axis: AxisId;
    seriesId: string;
    color: string;
    // How the metric is drawn: a curve, or bins. What its comparison reference has to be drawn as
    // to be comparable with it - see comparedAsBins, which is the only thing that reads this.
    shape: 'line' | 'column';
    // The domain its toggle belongs to, so the toggle row can rule one group off from the next.
    group: 'growth' | 'water' | 'count';
    sensor: MetricSensor | null;
    // What the chart opens showing.
    defaultOn: boolean;
    labelKey: string;
    // What the metric is measured in, wherever a figure of it is written down - its axis caption and
    // its cursor label. Null for one that is not a measurement at all, and a function of the
    // granularity for one whose unit genuinely changes with it.
    unitKey: (granularity: ProductivityGranularity) => string | null;
    // The plain-language account of what this metric actually plots, carried on the control that
    // turns it on: the derived figures' names do not explain themselves and the chart has nowhere
    // else to say so.
    explanationKey: string;
    // How many decimals a figure of this metric is written with, wherever it is written down. Its
    // own measurement precision, not whatever the raw value happens to carry: left to the locale's
    // two-decimal default, one row printed weight as "52,8" beside a Growth Index of "86,03",
    // claiming a hundredth of an index point from a derivation that has no such thing - and printed
    // a water pair as "6,5" against "6,35", where the two figures exist only to be read against each
    // other. A function of the granularity for the one metric whose unit changes with it.
    decimals: (granularity: ProductivityGranularity) => number;
    // The bucket field this metric is read from - the one that decides whether its band holds
    // anything at all across the selected range. A function of the granularity because water states
    // its reading in a different field at each one.
    //
    // One field, not a list. Every metric has exactly one, and the list form meant every caller
    // took element zero and cast it back to a single field - three casts to undo a plural that never
    // happened.
    field: (granularity: ProductivityGranularity) => ProductivityBucketField;
    // The grain the metric is actually published at, as opposed to the grain the chart was asked
    // for. Four of the six report as often as the read asks. Two are calendar-day figures with no
    // finer form: the Water Norm is published per day, and a dead count is a day's tally of pig
    // movements. At Hour that difference is visible in the response - those two arrive on a bucket
    // of the day's own rather than on any hour's (see the API's MergeBuckets) - so a metric
    // published per day is read from the day the hovered hour falls in, and drawn a day wide, while
    // its neighbours are read from the hour itself. Stated here because both of those consumers
    // need it and neither is a good place for the other to learn it from.
    publishedGrain: 'bucket' | 'day';
    // The band outside which a figure of this metric is a derivation artefact rather than a reading,
    // or null for a metric measured directly enough to have none. Only the two day-over-day
    // derivations do: a mid-period restock makes them briefly non-physical, and one such point scales
    // an axis into uselessness (see plausibleRange.ts). Declared here rather than named at each site
    // that filters, because the sites are many - the series, the readout row, its reference line, the
    // withheld-point caveat, and the comparison halo, which shares the derivations' own axes and is
    // the easiest of them to overlook.
    plausibleRange: PlausibleRange | null;
    // Whether the current granularity can express this metric at all, as opposed to whether the
    // reader has asked for it. The Water Norm is not merely absent at hourly, it is meaningless
    // there, and the backend returns nothing for it:
    //   - Water Norm at Hour: published per calendar day, so it simply has no hourly form. Metered
    //     water does, and is drawn there in raw litres rather than per pig, for the same reason -
    //     the pig tally that would divide it is itself a daily series.
    availableAtGranularity: (granularity: ProductivityGranularity) => boolean;
    // What to tell the reader when the current granularity cannot express this metric. Null for a
    // metric every granularity can.
    unavailableAtGranularityKey: string | null;
    // Whether a figure of this metric for a period that has not finished yet is comparable to the
    // finished periods drawn beside it. False for the two that are per-pig daily rates: metered
    // litres divided by that day's pig tally is a rate only once the day has run, and until then the
    // numerator is however much of the day has elapsed while its neighbours carry a whole one. Drawn
    // as-is, today's column reads as a collapse in consumption every morning and heals itself
    // overnight - on one Section, 4,1 l/pig/day at midday against 9,7 the day before - and the norm
    // beside it, which exists only to be read against that rate, turns a partial figure into a
    // deviation nobody can act on.
    //
    // Deliberately not extended to the hourly metered total, which is understated for the current
    // hour by exactly the same arithmetic: it is one bucket in some thousands rather than one in
    // ninety, it is the raw reading rather than a derivation, and the hourly view is where the
    // partial litres are the honest answer to "how much so far today". Nor to the dead count, where
    // a day's running tally is a true statement about that day. Weight needs no rule here at all -
    // its day figure is the reading in the Directory's own midday hour, so a day before midday
    // simply has none (see the API's SelectRepresentativeDailyRows).
    meaningfulForPartialPeriod: (granularity: ProductivityGranularity) => boolean;
};

const T = 'views.monitoring.activeGroups';

export const PRODUCTIVITY_METRICS = {
    weight: {
        band: 'weight',
        axis: 'weight',
        seriesId: 'productivityWeight',
        color: CHART_SERIES_COLORS.weight,
        shape: 'line',
        group: 'growth',
        sensor: 'weight',
        defaultOn: true,
        labelKey: `${T}.stream.weight`,
        unitKey: () => `${T}.texts.weightUnit`,
        explanationKey: `${T}.modals.productivity.explainWeight`,
        // A hundred grams, which is about what a Sensor-derived mean weight is good for.
        decimals: () => 1,
        field: () => 'weight_kg',
        publishedGrain: 'bucket',
        plausibleRange: null,
        availableAtGranularity: () => true,
        unavailableAtGranularityKey: null,
        meaningfulForPartialPeriod: () => true,
    },
    // On by default, and free to be: it shares the weight band rather than opening one of its own,
    // so growth arrives beside the weight it was computed from at no cost in chart height.
    dailyGain: {
        band: 'weight',
        axis: 'dailyGain',
        seriesId: 'productivityDailyGain',
        color: CHART_SERIES_COLORS.dailyGain,
        shape: 'line',
        group: 'growth',
        sensor: 'weight',
        defaultOn: true,
        labelKey: `${T}.tables.headings.dailyGain`,
        unitKey: () => `${T}.texts.dailyGainUnit`,
        explanationKey: `${T}.modals.productivity.explainDailyGain`,
        // Already in grams per day; a tenth of a gram is noise from the day-over-day subtraction.
        decimals: () => 0,
        field: () => 'daily_gain_g',
        publishedGrain: 'bucket',
        plausibleRange: DAILY_GAIN_G_RANGE,
        availableAtGranularity: () => true,
        unavailableAtGranularityKey: null,
        meaningfulForPartialPeriod: () => true,
    },
    growthIndex: {
        band: 'growthIndex',
        axis: 'growthIndex',
        seriesId: 'productivityGrowthIndex',
        color: CHART_SERIES_COLORS.growthIndex,
        shape: 'line',
        group: 'growth',
        sensor: 'weight',
        defaultOn: true,
        labelKey: `${T}.fields.growthIndex`,
        // Deliberately unitless - a ratio against a growth target, not a measurement.
        unitKey: () => null,
        explanationKey: `${T}.modals.productivity.explainGrowthIndex`,
        // Whole index points, as the Section grid's own gauge already prints it.
        decimals: () => 0,
        field: () => 'growth_index',
        publishedGrain: 'bucket',
        plausibleRange: GROWTH_INDEX_RANGE,
        availableAtGranularity: () => true,
        unavailableAtGranularityKey: null,
        meaningfulForPartialPeriod: () => true,
    },
    water: {
        band: 'water',
        axis: 'water',
        seriesId: 'productivityWaterActual',
        color: CHART_SERIES_COLORS.waterActual,
        shape: 'column',
        group: 'water',
        sensor: 'water',
        defaultOn: true,
        labelKey: `${T}.stream.water`,
        // Litres for an hour bucket, litres per pig per day for a day bucket - the band genuinely
        // changes unit with the granularity, so the caption has to say which.
        unitKey: (granularity: ProductivityGranularity) => (granularity === 'hour' ? `${T}.texts.waterUnitLitres` : `${T}.texts.waterUnit`),
        explanationKey: `${T}.modals.productivity.explainWater`,
        // Whole litres for an hour's metered total, a tenth of a litre for the per-pig daily figure -
        // the same decimal the norm it is read against uses.
        decimals: (granularity: ProductivityGranularity) => (granularity === 'hour' ? 0 : 1),
        field: (granularity: ProductivityGranularity) => waterActualKey(granularity),
        publishedGrain: 'bucket',
        plausibleRange: null,
        availableAtGranularity: () => true,
        unavailableAtGranularityKey: null,
        meaningfulForPartialPeriod: (granularity: ProductivityGranularity) => granularity !== 'day',
    },
    waterNorm: {
        band: 'water',
        axis: 'water',
        seriesId: 'productivityWaterNorm',
        color: CHART_SERIES_COLORS.waterNorm,
        shape: 'column',
        group: 'water',
        sensor: 'water',
        defaultOn: true,
        labelKey: `${T}.stream.waterNorm`,
        unitKey: (granularity: ProductivityGranularity) => (granularity === 'hour' ? `${T}.texts.waterUnitLitres` : `${T}.texts.waterUnit`),
        explanationKey: `${T}.modals.productivity.explainWaterNorm`,
        decimals: () => 1,
        field: () => 'norm_water_per_pig',
        publishedGrain: 'day',
        plausibleRange: null,
        availableAtGranularity: (granularity: ProductivityGranularity) => granularity === 'day',
        unavailableAtGranularityKey: `${T}.modals.productivity.waterNormDayOnly`,
        meaningfulForPartialPeriod: () => false,
    },
    // Off by default, and the one metric that opens a band of its own to be shown at all - so the
    // chart stays quiet until that question is asked. Never Sensor-gated: a dead count comes from
    // pig movements rather than from telemetry.
    deadCount: {
        band: 'deadCount',
        axis: 'deadCount',
        seriesId: 'productivityDeadCount',
        color: CHART_SERIES_COLORS.deadCount,
        shape: 'column',
        group: 'count',
        sensor: null,
        defaultOn: false,
        labelKey: `${T}.modals.productivity.deadCountBand`,
        unitKey: () => `${T}.texts.deadUnit`,
        explanationKey: `${T}.modals.productivity.explainDeadCount`,
        // A count of pigs, whole at every granularity.
        decimals: () => 0,
        field: () => 'dead_count',
        publishedGrain: 'day',
        plausibleRange: null,
        availableAtGranularity: () => true,
        unavailableAtGranularityKey: null,
        meaningfulForPartialPeriod: () => true,
    },
} as const satisfies Record<string, MetricDefinition>;

export type MetricKey = keyof typeof PRODUCTIVITY_METRICS;

/**
 * How many decimals a figure of one metric is written with at one granularity.
 *
 * Exists so surfaces outside the Productivity Chart - the Section grid's cells, the weight modal's cards
 * and its tooltip - write a figure to the precision this registry already gives it, instead of falling
 * back to the locale's `decimal` default of "up to two". That default puts a Growth Index on screen as
 * "86,03", claiming a hundredth of an index point from a derivation that has none.
 *
 * Typed through the declared shape rather than the literal, so a metric whose precision does not vary
 * still takes a granularity here and no call site changes when one starts varying.
 */
export function metricDecimals(key: MetricKey, granularity: ProductivityGranularity): number {
    const metric: MetricDefinition = PRODUCTIVITY_METRICS[key];
    return metric.decimals(granularity);
}

// Declaration order is the order the toggle chips are laid out in and the order the readout row's
// per-series hover marks are placed in - the growth trio, then the water pair, then the count.
export const METRIC_KEYS = Object.keys(PRODUCTIVITY_METRICS) as MetricKey[];

/**
 * One boolean per metric. The chart's own toggles, the band layout's visibility input and the
 * comparison halo's visibility input are all this same shape, because a halo is drawn for exactly
 * the metrics the chart is drawing and a band is shown for exactly the metrics that live in it -
 * three separate declarations of the same six keys is three places for a seventh metric to be
 * forgotten.
 */
export type MetricFlags = Record<MetricKey, boolean>;

// Every bucket field any metric is read from. Stated here rather than derived from the registry:
// the registry's own definitions are typed against this, so deriving it from them would be circular.
export type ProductivityBucketField = 'weight_kg' | 'daily_gain_g' | 'growth_index' | 'water_litres' | 'water_per_pig' | 'norm_water_per_pig' | 'dead_count';

export function metricKeysInBand(band: MetricBandId): MetricKey[] {
    return METRIC_KEYS.filter((key) => PRODUCTIVITY_METRICS[key].band === band);
}

/**
 * The bucket field a metric is read from at this granularity.
 *
 * Goes through the declared `MetricDefinition` rather than calling `PRODUCTIVITY_METRICS[key].field`
 * directly: the registry is `as const`, so indexing it with a `MetricKey` union yields a union of
 * function types, and only the members that ignore the granularity are 0-arity - which makes the
 * intersection of their signatures take no arguments at all. Widening to the declared shape asks the
 * one signature the definition actually promises.
 */
export function metricField(key: MetricKey, granularity: ProductivityGranularity): ProductivityBucketField {
    const definition: MetricDefinition = PRODUCTIVITY_METRICS[key];
    return definition.field(granularity);
}

/**
 * The bucket fields no bucket still in progress may state a figure in - see
 * `meaningfulForPartialPeriod`. Empty at every granularity but Day.
 */
export function partialPeriodBlankedFields(granularity: ProductivityGranularity): ProductivityBucketField[] {
    return METRIC_KEYS.filter((key) => !PRODUCTIVITY_METRICS[key].meaningfulForPartialPeriod(granularity)).map((key) => metricField(key, granularity));
}

/**
 * The bucket fields whose being null everywhere makes each band empty: every field of every metric
 * drawn in that band, since a band holding one of its two metrics still has something to show.
 */
export function bandEmptinessFields(granularity: ProductivityGranularity): Record<MetricBandId, ProductivityBucketField[]> {
    return Object.fromEntries(
        PRODUCTIVITY_BAND_IDS.map((band) => [band, metricKeysInBand(band).map((key) => metricField(key, granularity))])
    ) as Record<MetricBandId, ProductivityBucketField[]>;
}
